import { Container } from '@mui/material';
import { useMemo, useState } from 'react';
import { Navigate, Outlet, useOutletContext } from 'react-router-dom';

import Loading from '@/components/elements/Loading';
import ContextHeaderContent from '@/components/layout/dashboard/contextHeader/ContextHeaderContent';
import {
  useAdminBreadcrumbConfig,
  useDashboardBreadcrumbs,
} from '@/components/layout/dashboard/contextHeader/useDashboardBreadcrumbs';
import DashboardContentContainer from '@/components/layout/dashboard/DashboardContentContainer';
import { firstNavItemWithAccess } from '@/components/layout/dashboard/sideNav/navUtil';
import SideNavMenu from '@/components/layout/dashboard/sideNav/SideNavMenu';
import { NavItem } from '@/components/layout/dashboard/sideNav/types';
import NotFound from '@/components/pages/NotFound';
import { useDashboardState } from '@/hooks/useDashboardState';
import { useRootPermissions } from '@/modules/permissions/useHasPermissionsHooks';
import { AdminDashboardRoutes } from '@/routes/routes';
import { RootPermissionsFragment } from '@/types/gqlTypes';

const navItems: NavItem<RootPermissionsFragment>[] = [
  {
    id: 'admin-nav',
    type: 'category',
    items: [
      {
        id: 'denials',
        title: 'Denials',
        path: AdminDashboardRoutes.AC_DENIALS,
        permissions: ['canManageDeniedReferrals'],
      },
      {
        id: 'users',
        title: 'Users',
        path: AdminDashboardRoutes.USERS,
        permissions: ['canImpersonateUsers', 'canAuditUsers'],
        permissionMode: 'any',
      },
      {
        id: 'merge-clients',
        title: 'Client Merge History',
        path: AdminDashboardRoutes.CLIENT_MERGE_HISTORY,
        permissions: ['canMergeClients'],
      },
      {
        id: 'coordinated-entry',
        title: 'Coordinated Entry',
        path: AdminDashboardRoutes.COORDINATED_ENTRY,
        permissions: [
          'canAdministrateCoordinatedEntry',
          'canViewCoordinatedEntry',
        ],
        permissionMode: 'all',
      },
    ],
  },
  {
    id: 'config',
    title: 'Config',
    type: 'category',
    items: [
      {
        id: 'forms',
        title: 'Forms',
        path: AdminDashboardRoutes.FORMS,
        permissions: ['canConfigureDataCollection'],
      },
      {
        id: 'services',
        title: 'Services',
        path: AdminDashboardRoutes.CONFIGURE_SERVICES,
        permissions: ['canConfigureDataCollection'],
      },
      {
        id: 'project-config',
        title: 'Project',
        path: AdminDashboardRoutes.PROJECT_CONFIG,
        permissions: ['canConfigureDataCollection'],
      },
    ],
  },
];

export const PERMISSIONS_GRANTING_ADMIN_DASHBOARD_ACCESS = navItems
  .flatMap((group) => group.items)
  .flatMap((item) => item?.permissions || []);

// redirect to whichever admin page that the user has access to
export const AdminLandingPage = () => {
  const [access, { loading, error }] = useRootPermissions();

  const pageWithAccess = useMemo(
    () => (access ? firstNavItemWithAccess(navItems, access) : undefined),
    [access]
  );

  if (!access && loading) return <Loading />;

  if (error) throw error;
  if (!access) return null;

  if (pageWithAccess?.path) {
    return <Navigate to={pageWithAccess.path} replace />;
  }

  return null;
};

const AdminDashboard: React.FC = () => {
  const [access] = useRootPermissions();
  const { noPadding, ...dashboardState } = useDashboardState();
  // allow pages to "override" the default breadcrumb text
  const [breadcrumbOverrides, overrideBreadcrumbTitles] = useState<
    Record<string, string> | undefined
  >();

  const breadCrumbConfig = useAdminBreadcrumbConfig();
  const breadcrumbs = useDashboardBreadcrumbs(
    breadCrumbConfig,
    breadcrumbOverrides
  );

  const outletContext = useMemo(() => {
    return {
      overrideBreadcrumbTitles,
    };
  }, []);
  if (!access) return <NotFound />;

  return (
    <DashboardContentContainer
      contextHeader={<ContextHeaderContent breadcrumbs={breadcrumbs} />}
      sidebar={
        <SideNavMenu<RootPermissionsFragment>
          items={navItems}
          access={access}
        />
      }
      noPadding={noPadding}
      navLabel={'Admin'}
      {...dashboardState}
    >
      <Container maxWidth={noPadding ? false : 'xl'} disableGutters>
        <Outlet context={outletContext} />
      </Container>
    </DashboardContentContainer>
  );
};

export type AdminDashboardContext = {
  overrideBreadcrumbTitles: (crumbs: any) => void;
};

export const useAdminDashboardContext = () => {
  return useOutletContext<AdminDashboardContext>();
};

export default AdminDashboard;
